import {getCustomSEO} from '../seo';
import {stripHTML} from '../../string.helper';

/**
 * Tạo SEO cho trang danh sách tuyển dụng
 */
export function generateRecruitmentListSEO(recruitments, totalCount, webUrl) {
    const recruitmentListUrl = `${webUrl}/tuyen-dung`;

    // Tạo title
    const seoTitle =
        totalCount && totalCount > 0
            ? `Tuyển dụng - ${totalCount} vị trí | CTCP Tập đoàn Bệnh viện TNH`
            : 'Tuyển dụng | CTCP Tập đoàn Bệnh viện TNH';

    // Tạo description từ danh sách vị trí tuyển dụng
    let seoDescription = '';
    if (recruitments && Array.isArray(recruitments) && recruitments.length > 0) {
        // Lấy tên các vị trí chính (tối đa 4-5)
        const mainPositions = recruitments
            .slice(0, 5)
            .map(job => {
                const title = job.recruitment_name || job.title || '';
                // Loại bỏ HTML tags và giới hạn độ dài
                return stripHTML(title, 50);
            })
            .filter(Boolean);

        const positionTitles = mainPositions.join(', ');
        const count = totalCount || recruitments.length;

        seoDescription = `CTCP Tập đoàn Bệnh viện TNH tuyển dụng ${count} vị trí việc làm: ${positionTitles}${count > 5 ? ' và nhiều vị trí khác' : ''}. Cơ hội nghề nghiệp tại bệnh viện hàng đầu, môi trường làm việc chuyên nghiệp, chế độ đãi ngộ hấp dẫn. Nộp hồ sơ ngay!`;
    } else {
        seoDescription =
            'CTCP Tập đoàn Bệnh viện TNH tuyển dụng nhân sự. Cơ hội nghề nghiệp tại bệnh viện hàng đầu với môi trường làm việc chuyên nghiệp, chế độ đãi ngộ hấp dẫn. Xem danh sách vị trí tuyển dụng và nộp hồ sơ ngay!';
    }

    // Giới hạn description
    if (seoDescription.length > 160) {
        seoDescription = seoDescription.substring(0, 157) + '...';
    }

    // Tạo keywords từ danh sách tuyển dụng
    const keywordsParts = [];
    if (recruitments && Array.isArray(recruitments)) {
        // Lấy keywords từ các vị trí chính (tối đa 10)
        recruitments.slice(0, 10).forEach(job => {
            // Thêm tên vị trí
            if (job.recruitment_name) {
                keywordsParts.push(job.recruitment_name);
            }

            // Thêm position name nếu có
            if (job.position_name && !keywordsParts.includes(job.position_name)) {
                keywordsParts.push(job.position_name);
            }

            // Thêm department name
            if (job.department_name && !keywordsParts.includes(job.department_name)) {
                keywordsParts.push(job.department_name);
            }

            // Thêm employment type
            if (job.employment_type_name && !keywordsParts.includes(job.employment_type_name)) {
                keywordsParts.push(job.employment_type_name);
            }

            // Thêm business name
            if (job.business_name && !keywordsParts.includes(job.business_name)) {
                keywordsParts.push(job.business_name);
            }

            // Thêm từ title
            const title = job.recruitment_name || job.title || '';
            if (title) {
                const cleanTitle = stripHTML(title);
                // Lấy các từ quan trọng từ title
                const words = cleanTitle
                    .split(/\s+/)
                    .filter(
                        word =>
                            word.length > 3 &&
                            word.length < 20 &&
                            !['và', 'của', 'cho', 'với', 'từ', 'tại', 'theo', 'vị', 'trí'].includes(word.toLowerCase()),
                    )
                    .slice(0, 3);
                keywordsParts.push(...words);
            }
        });
    }

    // Thêm keywords mặc định cho tuyển dụng
    keywordsParts.push(
        'Tuyển dụng',
        'Việc làm',
        'CTCP Tập đoàn Bệnh viện TNH',
        'Bệnh viện TNH',
        'Tuyển dụng y tế',
        'Cơ hội nghề nghiệp',
        'Nhân sự y tế',
    );
    const seoKeywords = [...new Set(keywordsParts)].join(', '); // Loại bỏ duplicate

    // Xử lý image - lấy ảnh từ vị trí đầu tiên hoặc dùng default
    let seoImage = '';
    if (recruitments && Array.isArray(recruitments) && recruitments.length > 0) {
        const firstJob = recruitments.find(
            job => job.image_url || job.banner_url || job.department_image || job.business_logo,
        );
        if (firstJob) {
            seoImage =
                firstJob.image_url || firstJob.banner_url || firstJob.department_image || firstJob.business_logo || '';
            if (Array.isArray(seoImage)) {
                seoImage = seoImage[0] || '';
            }
            // Đảm bảo URL đầy đủ
            if (seoImage && !seoImage.startsWith('http://') && !seoImage.startsWith('https://')) {
                seoImage = seoImage.startsWith('/') ? `${webUrl}${seoImage}` : `${webUrl}/${seoImage}`;
            }
        }
    }

    return getCustomSEO(seoTitle, seoDescription, recruitmentListUrl, seoKeywords, seoImage);
}

/**
 * Tạo SEO cho trang chi tiết tuyển dụng
 */
export function generateRecruitmentSEO(recruitment, slug, webUrl) {
    const recruitmentUrl = `${webUrl}/${slug}`;

    // Xử lý title - thêm brand và format chuẩn
    const seoTitle = recruitment?.recruitment_name
        ? `${recruitment.recruitment_name} | Tuyển dụng TNH`
        : 'Tuyển dụng TNH';

    // Xử lý description - kết hợp nhiều nguồn và strip HTML
    let seoDescription = '';

    // Kết hợp introduction + job_description + requirements để có mô tả đầy đủ hơn
    const introText = recruitment?.introduction ? stripHTML(recruitment.introduction) : '';
    const jobDescText = recruitment?.job_description ? stripHTML(recruitment.job_description) : '';
    const requirementsText = recruitment?.requirements ? stripHTML(recruitment.requirements) : '';

    // Kết hợp các phần thông tin để có description đầy đủ nhất
    const textParts = [];
    if (introText) textParts.push(introText);
    if (jobDescText) textParts.push(jobDescText);
    // Chỉ thêm requirements nếu description còn ngắn
    if (textParts.length < 2 && requirementsText) {
        textParts.push(requirementsText);
    }

    if (textParts.length > 0) {
        const combined = textParts.join('. ');
        if (combined.length <= 160) {
            seoDescription = combined;
        } else {
            // Cắt từng phần để giữ lại thông tin quan trọng nhất
            let result = '';
            for (const part of textParts) {
                if (result.length + part.length + 2 <= 157) {
                    result = result ? `${result}. ${part}` : part;
                } else {
                    break;
                }
            }
            seoDescription = result || stripHTML(textParts[0], 157);
            if (seoDescription.length > 157) {
                seoDescription = seoDescription.substring(0, 157) + '...';
            }
        }
    }

    // Nếu vẫn chưa có description đủ dài, tạo từ thông tin có sẵn
    if (!seoDescription || seoDescription.length < 100) {
        const parts = [];
        if (recruitment?.recruitment_name) parts.push(`Vị trí ${recruitment.recruitment_name}`);
        if (recruitment?.business_name) parts.push(`tại ${recruitment.business_name}`);
        if (recruitment?.department_name) parts.push(`- ${recruitment.department_name}`);
        if (recruitment?.employment_type_name) parts.push(`(${recruitment.employment_type_name})`);
        if (recruitment?.salary) {
            const salaryFormatted = parseInt(recruitment.salary).toLocaleString('vi-VN');
            parts.push(`- Mức lương: ${salaryFormatted} VNĐ`);
        }
        parts.push('Ứng tuyển ngay tại CTCP Tập đoàn Bệnh viện TNH.');

        seoDescription = parts.join(' ');
        if (seoDescription.length > 160) {
            seoDescription = seoDescription.substring(0, 157) + '...';
        }
    }

    // Fallback cuối cùng
    if (!seoDescription) {
        seoDescription = 'Cơ hội việc làm tại CTCP Tập đoàn Bệnh viện TNH. Đăng ký ứng tuyển ngay hôm nay.';
    }

    // Xử lý keywords - thêm thông tin đầy đủ
    const keywordsParts = [];
    if (recruitment?.recruitment_name) keywordsParts.push(recruitment.recruitment_name);
    if (recruitment?.position_name) keywordsParts.push(recruitment.position_name);
    if (recruitment?.department_name) keywordsParts.push(recruitment.department_name);
    if (recruitment?.business_name) keywordsParts.push(recruitment.business_name);
    if (recruitment?.employment_type_name) keywordsParts.push(recruitment.employment_type_name);

    // Thêm location từ business_name nếu có
    if (recruitment?.business_name) {
        const businessLower = recruitment.business_name.toLowerCase();
        if (businessLower.includes('thái nguyên')) keywordsParts.push('Thái Nguyên');
        if (businessLower.includes('bắc giang')) keywordsParts.push('Bắc Giang');
        if (businessLower.includes('bắc ninh')) keywordsParts.push('Bắc Ninh');
        if (businessLower.includes('phổ yên')) keywordsParts.push('Phổ Yên');
        if (businessLower.includes('việt yên')) keywordsParts.push('Việt Yên');
    }

    keywordsParts.push('Tuyển dụng', 'TNH', 'Việc làm', 'Ứng tuyển', 'Tuyển nhân viên');

    // Nếu có meta_keywords từ backend, ưu tiên dùng nó nhưng vẫn merge thêm
    const seoKeywords = recruitment?.meta_keywords
        ? `${recruitment.meta_keywords}, ${keywordsParts.join(', ')}`
        : keywordsParts.join(', ');

    // Xử lý image - ưu tiên image_url, banner_url, sau đó thử lấy từ department/business
    let seoImage = recruitment?.image_url || recruitment?.banner_url;

    // Nếu không có image, thử fallback từ department logo hoặc business logo
    if (!seoImage && recruitment?.department_image) {
        seoImage = recruitment.department_image;
    }
    if (!seoImage && recruitment?.business_logo) {
        seoImage = recruitment.business_logo;
    }

    // Cuối cùng mới dùng logo mặc định (để getCustomSEO xử lý)
    if (!seoImage) {
        seoImage = '';
    }

    return getCustomSEO(seoTitle, seoDescription, recruitmentUrl, seoKeywords, seoImage);
}
